import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { DualAuthService } from '../../services/DualAuthService';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { RoleService } from '../../services/RoleService';
import { AuthUser, UserType, EmployeeRole, Permission } from '../../models/AuthUser';
import { PageHeaderComponent } from "../page-header/page-header.component";

interface SessionAccount {
  name?: string;
  email?: string;
  givenName?: string;
  familyName?: string;
  userType?: 'employee' | 'customer';
}

interface TokenInfo {
  accessToken?: string;
  idToken?: string;
  account?: SessionAccount;
  scopes?: string[];
  expiresOn?: Date;
  claims?: Array<any>;
  groups?: string[];
  roles?: EmployeeRole[];
  permissions?: Permission[];
  customerProfile?: any;
}

@Component({
  selector: 'app-token-info',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatChipsModule,
    MatExpansionModule,
    MatTooltipModule,
    MatSnackBarModule,
    PageHeaderComponent
],
  templateUrl: './token-info.component.html',
  styleUrl: './token-info.component.css'
})
export class TokenInfoComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private readonly apiBase = `${environment.backend.baseUrl}${environment.backend.apiUrl}`;

  currentUser: AuthUser | null = null;
  tokenInfo: TokenInfo = {};
  isAuthenticated = false;

  // Expose enums and global objects to template
  userTypeEnum = UserType;
  employeeRoleEnum = EmployeeRole;
  permissionEnum = Permission;
  Object = Object;
  JSON = JSON;

  constructor(
    private dualAuthService: DualAuthService,
    private http: HttpClient,
    private roleService: RoleService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    // Subscribe to authentication state
    this.dualAuthService.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAuth => {
        this.isAuthenticated = isAuth;
        if (isAuth) {
          this.loadTokenInformation();
        }
      });

    // Subscribe to current user
    this.dualAuthService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        if (user) {
          this.loadTokenInformation();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadTokenInformation(): Promise<void> {
    if (!this.isAuthenticated || !this.currentUser) {
      return;
    }

    try {
      if (this.currentUser.userType === UserType.EMPLOYEE) {
        const who: any = await firstValueFrom(
          this.http.get(`${this.apiBase}/whoami`, { withCredentials: true })
        );
        let roleInfo: any = null;
        try {
          roleInfo = await firstValueFrom(
            this.http.get(`${this.apiBase}/whoami/roles`, { withCredentials: true })
          );
        } catch { roleInfo = null; }

        const roles = (this.currentUser.roles as EmployeeRole[]) || [];
        const permissions = (this.currentUser.permissions as Permission[]) || [];
        const groups = Array.isArray(roleInfo?.groups) ? roleInfo.groups : [];
        this.tokenInfo = {
          account: {
            name: (who?.profile?.name || who?.name || this.currentUser.email),
            email: (who?.profile?.email || who?.email || this.currentUser.email),
            givenName: (who?.profile?.givenName || who?.givenName || this.currentUser.firstName),
            familyName: (who?.profile?.familyName || who?.familyName || this.currentUser.lastName),
            userType: 'employee'
          },
          claims: [],
          roles,
          groups,
          permissions
        } as TokenInfo;
        return;
      } else {
        // Customer: show server identity and profile
        const who: any = await firstValueFrom(
          this.http.get(`${this.apiBase}/whoami`, { withCredentials: true })
        );
        const me: any = await firstValueFrom(
          this.http.get(`${this.apiBase}/me`, { withCredentials: true })
        );
        let profile: any = null;
        try {
          // Use /api/User/me which returns customer in the response
          const userMeResponse: any = await firstValueFrom(
            this.http.get(`${this.apiBase}/User/me`, { withCredentials: true })
          );
          profile = userMeResponse?.customer || null;
        } catch { profile = null; }

        const claims = me?.claims || [];
        const name = (who?.profile?.name || who?.name || this.currentUser.email);
        const groups = [] as string[]; // customers typically do not carry AAD groups
        const roles = (this.currentUser.roles as EmployeeRole[]) || [];
        const permissions = (this.currentUser.permissions as Permission[]) || [];
        this.tokenInfo = {
          account: {
            name,
            email: (who?.profile?.email || who?.email || this.currentUser.email),
            givenName: (who?.profile?.givenName || who?.givenName || this.currentUser.firstName),
            familyName: (who?.profile?.familyName || who?.familyName || this.currentUser.lastName),
            userType: 'customer'
          },
          claims,
          roles,
          groups,
          permissions,
          customerProfile: profile
        } as TokenInfo;
      }

    } catch (error) {
      console.error('Error loading token information:', error);
      this.snackBar.open('Error loading token information', 'Close', { duration: 3000 });
    }
  }


  copyToClipboard(text: string, label: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.snackBar.open(`${label} copied to clipboard`, 'Close', { duration: 2000 });
    }).catch(err => {
      console.error('Could not copy text: ', err);
      this.snackBar.open('Failed to copy to clipboard', 'Close', { duration: 2000 });
    });
  }

  formatDate(date: Date | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  }

  getRoleDisplayName(role: EmployeeRole): string {
    return this.roleService.getRoleDisplayName(role);
  }

  getPermissionDisplayName(permission: Permission): string {
    // Convert enum value to display name
    return permission.replace(/_/g, ' ').toLowerCase()
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  refreshTokenInfo(): void {
    this.loadTokenInformation();
    this.snackBar.open('Token information refreshed', 'Close', { duration: 2000 });
  }
}
